import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Box, Typography } from '@mui/material';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import HotelIcon from '@mui/icons-material/Hotel';
import LandscapeIcon from '@mui/icons-material/Landscape';
import SpaIcon from '@mui/icons-material/Spa';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';

const ICONS = { tour: LandscapeIcon, wellness: SpaIcon, hotel: HotelIcon, transport: DirectionsBusIcon };

// string | {url} | array of either  ->  clean array of URLs
export const normalizeImages = (images) =>
  (Array.isArray(images) ? images : [images])
    .map((i) => (typeof i === 'string' ? i : i?.url))
    .filter(Boolean);

export const pickImage = (images) => normalizeImages(images)[0] || null;

// ── Image resizing proxy ─────────────────────────────────────────────────────
// Firebase Storage can't resize images, so thumbnails are requested through a resizing
// proxy (wsrv.nl by default). To switch provider (e.g. Cloudflare) change ONLY this function.
// Set USE_IMAGE_PROXY to false to go back to loading the original files.
const USE_IMAGE_PROXY = true;
// If the proxy has not delivered the image in this long, fall back to the original file
// so a slow / rate-limited proxy can never leave a card blank.
const PROXY_TIMEOUT_MS = 6000;
const isFirebaseUrl = (u) => /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\//.test(u);

export const thumbUrl = (url, width) => {
  if (!USE_IMAGE_PROXY || !url || !width || typeof url !== 'string' || !isFirebaseUrl(url)) return url;
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&w=${width}&output=webp&q=75&we`;
};

export const NoImage = ({ type, message = 'No Image Available', compact = false, sx }) => {
  const Icon = ICONS[type] || ImageNotSupportedOutlinedIcon;
  return (
    <Box
      role="img" aria-label={message} title={message}
      sx={{
        width: '100%', height: '100%', minHeight: compact ? 0 : 80,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
        bgcolor: 'grey.100', color: 'text.secondary', textAlign: 'center', p: compact ? 0 : 1, ...sx,
      }}
    >
      <Icon sx={{ fontSize: compact ? 22 : 32, opacity: 0.7 }} />
      {!compact && (
        <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1.2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

// `width` (px) = the size the image is actually displayed at; used to request a smaller file.
// Default width so EVERY SafeImage (tours, wellness, hotels, transport...) is resized automatically,
// even where no width is passed. Pass a smaller width for thumbnails, or width={0} to load the original.
const DEFAULT_WIDTH = 800;

const SafeImage = ({ src, alt = '', type, message, compact, sx, width = DEFAULT_WIDTH, fade = false, ...imgProps }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [useOriginal, setUseOriginal] = useState(false);   // proxy failed/slow -> load the original file
  const imgRef = useRef(null);

  // Reset only when the source really changes
  useEffect(() => { setFailed(false); setLoaded(false); setUseOriginal(false); }, [src]);

  const proxied = thumbUrl(src, width);
  const canFallBack = !!src && proxied !== src;

  // Image may already be in the browser cache before React attaches onLoad
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [src, useOriginal]);

  // Slow proxy -> switch to the original file
  useEffect(() => {
    if (!canFallBack || useOriginal || loaded || failed) return undefined;
    const t = setTimeout(() => setUseOriginal(true), PROXY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [canFallBack, useOriginal, loaded, failed, src]);

  const onError = useCallback(() => {
    if (useOriginal || !canFallBack) setFailed(true);
    else setUseOriginal(true);
  }, [useOriginal, canFallBack]);
  const onLoad = useCallback(() => setLoaded(true), []);

  if (!src || failed) return <NoImage type={type} message={message} compact={compact} sx={sx} />;

  return (
    <Box
      component="img"
      ref={imgRef}
      src={useOriginal ? src : proxied}
      alt={alt}
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={onError}
      onLoad={onLoad}
      sx={{
        width: '100%', height: '100%', objectFit: 'cover', display: 'block',
        ...(fade ? { opacity: loaded ? 1 : 0, transition: 'opacity .25s ease' } : null),
        ...sx,
      }}
      {...imgProps}
    />
  );
};

export default memo(SafeImage);